<script setup lang="ts">
import HrefCreateButton from '@/components/Buttons/HrefCreateButton.vue'
import FullTableLayout from '@/components/Layouts/FullTableLayout.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import BaseTable from '@/components/Table/BaseTable.vue'
import TableActionColumn from '@/components/Table/TableActionColumn.vue'
import TableColumn from '@/components/Table/TableColumn.vue'
import TablePagination from '@/components/Table/TablePagination.vue'
import { ADMIN_DEVICE_TEMPLATES_ROUTES } from '@/router/routing/adminRouting/adminRoutes'
import store from '@/store'
import { EDeviceConfigActions } from '@/store/deviceConfigStore/DeviceConfigStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { ESortBy, ESortByAlphabetical, ESortByDate, getSortByKey } from '@/types/enum/SortByEnum'
import {
    defaultAdminDeviceFiltersType,
    type AdminDeviceFiltersType
} from '@/types/filters/AdminFilters'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import { isCurrentPageOverTotal } from '@/utils/paginationUtils'
import { addToQuery, getQueryFilters, getQuerySort } from '@/utils/queryUtils'
import { getPageSize } from '@/utils/routeUtils'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import SelectorField from '../../../../components/Selector/SelectorField.vue'
import TypographyItem from '../../../../components/Typography/TypographyItem.vue'
import {
    EPropertyType,
    EPropertyTypeLabels,
    EPropertyTypeValues
} from '../../../../types/enum/PropertyTypeEnum'

type Props = {
    isCurrentTab: boolean
    permission?: PermissionsEnums
    createButtonHref: string
    editButtonHref: string
}
const props = defineProps<Props>()

const router = useRouter()
const { t } = useI18n()
const data = computed(() => store.state.deviceConfig.deviceConfigs)
const filters = ref<AdminDeviceFiltersType & { type?: EPropertyType }>({
    ...defaultAdminDeviceFiltersType,
    ...getQueryFilters()
})
const sort = ref<ESortBy>(getQuerySort() ?? ESortBy.DATE_RECENT)

const columns = computed(() => ({
    version: { field: 'version', label: t('deviceConfig.template.table.version') },
    device_type_name: {
        field: 'device_type_name',
        label: t('deviceConfig.template.table.deviceTypeName')
    },
    propertyType: {
        field: 'type',
        label: t('deviceConfig.template.table.propertyType')
    }
}))

const getDeviceConfigs = () =>
    store.dispatch(`${EStoreModules.DEVICE_CONFIG}/${EDeviceConfigActions.FETCH_DEVICE_CONFIGS}`, {
        filters: filters.value,
        sort: getSortByKey(sort.value),
        per_page: data.value.per_page ?? getPageSize(),
        page: props.isCurrentTab ? data.value.current_page : 1
    })

const goToCreate = () => router.push(ADMIN_DEVICE_TEMPLATES_ROUTES.createUrl)

const handleChangePageSize = (value: number) => {
    data.value.per_page = value
    data.value.current_page = 1
    getDeviceConfigs()
}
const handleChangePageNumber = (pageNumber: number) => {
    data.value.current_page = pageNumber
    getDeviceConfigs()
}
const handleChangeSortBy = (sortBy: ESortBy) => {
    sort.value = sortBy
    getDeviceConfigs()
}

const deleteDeviceConfig = (device: DeviceModel) => {
    store
        .dispatch(`${EStoreModules.DEVICE_CONFIG}/${EDeviceConfigActions.DELETE_DEVICE_CONFIG}`, {
            id: device.id,
            name: device.name
        })
        .then(() =>
            isCurrentPageOverTotal(
                data.value.total - 1,
                data.value.per_page,
                data.value.current_page,
                data.value.last_page
            )
                ? handleChangePageNumber(data.value.current_page - 1)
                : getDeviceConfigs()
        )
}

const handleChangeFilter = (value: { type: EPropertyType }) => {
    filters.value = { ...filters.value, ...value }
    getDeviceConfigs()
}

const table = ref()

watch(
    () => props.isCurrentTab,
    isCurrent => (isCurrent ? addToQuery({ page: data.value.current_page }, true) : null)
)
onBeforeUnmount(() => addToQuery({ page: 1 }, true))
</script>

<template>
    <StackLayout direction="column" :gap="8">
        <PermissionWrapper :permission="permission">
            <HrefCreateButton
                :href="createButtonHref"
                :label="$t('deviceConfig.template.buttons.createTemplate')"
                @click="goToCreate"
                class="align-self-end" />
        </PermissionWrapper>

        <FullTableLayout
            isResponsiveSmallScreen
            :orderOptions="[...ESortByAlphabetical, ...ESortByDate]"
            :sortBy="sort"
            @changeSortBy="handleChangeSortBy">
            <template #filters>
                <SelectorField
                    :label="$t('deviceConfig.templateKeys.model.type')"
                    :modelValue="filters?.type"
                    :options="
                        EPropertyTypeValues.map(value => ({
                            label: $t(EPropertyTypeLabels[value]),
                            value
                        }))
                    "
                    @change="handleChangeFilter({ type: $event })" />
            </template>
            <template #table>
                <BaseTable v-bind:ref="table" :data="data.data" :loading="data.isLoading">
                    <template #columns>
                        <TableColumn
                            field="device_type_name"
                            :label="columns['device_type_name'].label" />
                        <TableColumn field="version" :label="columns['version'].label" />
                        <TableColumn :label="columns['propertyType'].label" v-slot="props">
                            <TypographyItem
                                :label="
                                    $t(EPropertyTypeLabels[(props.row.type as EPropertyType) ?? ''])
                                " />
                        </TableColumn>
                        <TableActionColumn
                            @delete="deleteDeviceConfig"
                            :editPermission="permission"
                            :deletePermission="permission"
                            :editButtonHref="editButtonHref" />
                    </template>
                </BaseTable>
            </template>
            <template #pagination>
                <TablePagination
                    :perPage="data.per_page"
                    :currentPage="data.current_page"
                    :lastPage="data.last_page"
                    :total="data.total"
                    forcePropsCurrentPage
                    @change:pageSize="handleChangePageSize"
                    @change:pageNumber="handleChangePageNumber" />
            </template>

            <template #dialogContent>
                <slot name="dialogContent" />
            </template>
        </FullTableLayout>
    </StackLayout>
</template>
